import { useState, type FormEvent } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { useBrands, useDeleteBrand, useSaveBrand, type AdminBrand } from '@/lib/catalog-api';
import { errorMessage, fieldErrors, formatNumber } from '@/lib/utils';

interface FormState {
  name: string;
  slug: string;
  website: string;
  description: string;
  displayOrder: string;
  isActive: boolean;
  isFeatured: boolean;
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  website: '',
  description: '',
  displayOrder: '0',
  isActive: true,
  isFeatured: false,
};

export function BrandsPage() {
  const brands = useBrands();
  const remove = useDeleteBrand();
  const [editing, setEditing] = useState<AdminBrand | null | undefined>(undefined);

  return (
    <>
      <PageHeader
        title="Brands"
        description="Brands shown on product pages and in the storefront filters"
        action={
          <Button size="sm" onClick={() => setEditing(null)}>
            <Plus className="size-4" aria-hidden="true" />
            Add Brand
          </Button>
        }
      />

      {remove.isError && (
        <div className="mb-4">
          <ErrorAlert title="Could not delete that brand">{errorMessage(remove.error)}</ErrorAlert>
        </div>
      )}

      <Card>
        {brands.isPending && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-11 w-full" />
            ))}
          </div>
        )}

        {brands.isError && (
          <div className="p-4">
            <ErrorAlert title="Could not load brands">{errorMessage(brands.error)}</ErrorAlert>
          </div>
        )}

        {brands.data?.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="No brands yet"
              description="Add one to group products by manufacturer."
            />
          </div>
        )}

        {brands.data && brands.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Brand</Th>
                <Th>Slug</Th>
                <Th>Website</Th>
                <Th className="text-right">Products</Th>
                <Th>Flags</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {brands.data.map((brand) => (
                <tr key={brand.id} className="hover:bg-canvas">
                  <Td className="font-medium text-ink">{brand.name}</Td>
                  <Td className="font-mono text-xs text-ink-subtle">{brand.slug}</Td>
                  <Td className="text-ink-muted">
                    {brand.website ? (
                      <a
                        href={brand.website}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-brand-600 hover:underline"
                      >
                        {brand.website.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className="text-right text-ink-muted">{formatNumber(brand.productCount)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <Badge tone={brand.isActive ? 'success' : 'neutral'}>
                        {brand.isActive ? 'Active' : 'Hidden'}
                      </Badge>
                      {brand.isFeatured && <Badge tone="accent">Featured</Badge>}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(brand)}
                        aria-label={`Edit ${brand.name}`}
                        className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete "${brand.name}"?`)) remove.mutate(brand.id);
                        }}
                        aria-label={`Delete ${brand.name}`}
                        className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <BrandDrawer
        open={editing !== undefined}
        brand={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
    </>
  );
}

/** Mounted only while open, so its state resets by unmounting. */
function BrandDrawer({
  open,
  brand,
  onClose,
}: {
  open: boolean;
  brand: AdminBrand | null;
  onClose: () => void;
}) {
  if (!open) {
    return null;
  }

  return <BrandForm key={brand?.id ?? 'new'} brand={brand} onClose={onClose} />;
}

function BrandForm({ brand, onClose }: { brand: AdminBrand | null; onClose: () => void }) {
  const save = useSaveBrand();
  const [form, setForm] = useState<FormState>(() =>
    brand
      ? {
          name: brand.name,
          slug: brand.slug,
          website: brand.website ?? '',
          description: brand.description ?? '',
          displayOrder: String(brand.displayOrder),
          isActive: brand.isActive,
          isFeatured: brand.isFeatured,
        }
      : EMPTY,
  );

  const errors = fieldErrors(save.error);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    save.mutate(
      {
        id: brand?.id ?? null,
        body: {
          name: form.name,
          ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
          website: form.website.trim() || null,
          description: form.description || null,
          displayOrder: Number(form.displayOrder || 0),
          isActive: form.isActive,
          isFeatured: form.isFeatured,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={brand ? 'Edit brand' : 'New brand'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="brand-form" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <form id="brand-form" onSubmit={handleSubmit} className="space-y-4">
        {save.isError && <ErrorAlert title="Could not save">{errorMessage(save.error)}</ErrorAlert>}

        <Field label="Name" htmlFor="brand-name" required error={errors['name']}>
          <Input
            id="brand-name"
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
            required
            minLength={2}
            invalid={Boolean(errors['name'])}
          />
        </Field>

        <Field
          label="Slug"
          htmlFor="brand-slug"
          hint="Leave blank to generate from the name."
          error={errors['slug']}
        >
          <Input
            id="brand-slug"
            value={form.slug}
            onChange={(event) => setForm((f) => ({ ...f, slug: event.target.value }))}
          />
        </Field>

        <Field
          label="Website"
          htmlFor="brand-website"
          hint="The brand's own site, including https://"
          error={errors['website']}
        >
          <Input
            id="brand-website"
            type="url"
            placeholder="https://example.com"
            value={form.website}
            onChange={(event) => setForm((f) => ({ ...f, website: event.target.value }))}
            invalid={Boolean(errors['website'])}
          />
        </Field>

        <Field label="Description" htmlFor="brand-description">
          <Textarea
            id="brand-description"
            rows={3}
            value={form.description}
            onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
          />
        </Field>

        <Field label="Display order" htmlFor="brand-order">
          <Input
            id="brand-order"
            type="number"
            min={0}
            value={form.displayOrder}
            onChange={(event) => setForm((f) => ({ ...f, displayOrder: event.target.value }))}
          />
        </Field>

        <Checkbox
          label="Visible on the storefront"
          checked={form.isActive}
          onChange={(event) => setForm((f) => ({ ...f, isActive: event.target.checked }))}
        />
        <Checkbox
          label="Featured"
          checked={form.isFeatured}
          onChange={(event) => setForm((f) => ({ ...f, isFeatured: event.target.checked }))}
        />
      </form>
    </Drawer>
  );
}
